import { InputPinCode, InputText } from 'components';
import { firebaseApp } from 'configs/firebase';
import {
  browserPopupRedirectResolver,
  browserSessionPersistence,
  initializeAuth,
  PhoneAuthProvider,
  RecaptchaVerifier,
  signInWithCredential,
  signInWithPhoneNumber,
} from 'firebase/auth';
import { Button, InputWraper, useForm } from 'modules';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthRouteKeys } from 'routes/enums';
import { formatPhoneNumber } from 'utils/helpers/phoneNumber';
import { formSchemaOTP, formSchemaPN } from './config';
import { DEFAULT_COUNTDOWN_OTP } from 'configs';
import {
  useAuthControllerSendOtpByEmail,
  useAuthControllerVerifyOtpByEmail,
  useCheckPhoneNoQuery,
} from 'services';

type Props = {
  onChangeCallback?: (value: boolean) => void;
};

export enum TYPE_VERIFY {
  PHONE = 'phone',
  EMAIL = 'email',
}

const GetOTP = (props: Props): JSX.Element => {
  const { onChangeCallback } = props;

  const { mutateAsync: mutateAsyncSendOtpByEmail } =
    useAuthControllerSendOtpByEmail();
  const { mutateAsync: mutateAsyncVerifyOtpByEmail } =
    useAuthControllerVerifyOtpByEmail();

  const auth = initializeAuth(firebaseApp, {
    persistence: browserSessionPersistence,
    popupRedirectResolver: browserPopupRedirectResolver,
  });

  useEffect(() => {
    window.recaptchaVerifier = new RecaptchaVerifier(
      'recaptcha-container',
      { size: 'invisible' },
      auth,
    );
  }, []);

  const navigate = useNavigate();

  //
  const location = useLocation();

  const defaultPhoneNo = location.state?.phoneNumber || '';

  const initTypeVerify = location.state?.initTypeVerify || TYPE_VERIFY.PHONE;
  const onlyAllowPhone = location.state?.onlyAllowPhone || false;

  const [methodVerify, setMethoVerify] = useState<TYPE_VERIFY>(initTypeVerify);
  const [onlyAllowPhoneNo, setOnlyAllowPhoneNo] =
    useState<boolean>(onlyAllowPhone);
  const [phoneNumber, setPhoneNumber] = useState<string>(defaultPhoneNo);
  const {
    data,
    isFetching: isFetchingCheckPhoneNoQuery,
    refetch,
  } = useCheckPhoneNoQuery({
    phoneNumber,
  });
  //

  const [verificationId, setVerificationId] = useState<string>('');

  const [changeMethodVerify, setChangeMethodVerify] = useState<boolean>(false);

  const [countdown, setCountdown] = useState<number>(DEFAULT_COUNTDOWN_OTP);

  useEffect(() => {
    if (phoneNumber && phoneNumber.length >= 10) {
      refetch();
    }
  }, [phoneNumber]);

  useEffect(() => {
    if (countdown > 0) {
      const timerId = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);

      return () => clearTimeout(timerId);
    }
  }, [countdown]);

  useEffect(() => {
    if (!changeMethodVerify) return;
    handleResendOTP();
    setChangeMethodVerify(false);
  }, [changeMethodVerify, methodVerify]);

  useEffect(() => {
    if (!data) return;
    if (data && data.statusCode === 201) {
      setOnlyAllowPhoneNo(true);
      setMethoVerify(TYPE_VERIFY.PHONE);
    } else {
      setOnlyAllowPhoneNo(false);
      setMethoVerify(TYPE_VERIFY.EMAIL);
    }
  }, [data]);

  const handelChangePhoneNumber = () => {
    navigate(AuthRouteKeys.Login);
  };

  const handleVerifyPhoneNumber = async () => {
    var phoneNumber = getPhoneNumber('phoneNumber');
    if (!phoneNumber.value && !defaultPhoneNo) {
      toast.error('Vui lòng nhập số điện thoại');
    }
    // Check if phone number is valid
    if (methodVerify === TYPE_VERIFY.PHONE) {
      try {
        const appVerifier = window.recaptchaVerifier;
        const confirmationResult = await signInWithPhoneNumber(
          auth,
          formatPhoneNumber(defaultPhoneNo || phoneNumber.value),
          appVerifier as RecaptchaVerifier,
        );
        setVerificationId(confirmationResult.verificationId);
        toast.success('Gửi mã xác thực thành công');
      } catch (error: unknown) {
        toast.error(
          (error as Error)?.message ||
            'Đã có lỗi xảy ra, vui lòng thử lại sau.',
        );
      }
    } else {
      const res = await mutateAsyncSendOtpByEmail({
        phoneNumber: formatPhoneNumber(defaultPhoneNo || phoneNumber.value),
      });
      setVerificationId(res.email);
      toast.success('Gửi mã xác thực thành công');
    }
  };

  const handleResendOTP = () => {
    if (methodVerify == TYPE_VERIFY.PHONE) {
      setCountdown(DEFAULT_COUNTDOWN_OTP);
      handleVerifyPhoneNumber();
    } else if (methodVerify == TYPE_VERIFY.EMAIL) {
      setCountdown(DEFAULT_COUNTDOWN_OTP);
      handleVerifyPhoneNumber();
    }
  };

  const handleSubmitOTP = async ({ otp }: { otp: string }) => {
    if (methodVerify == TYPE_VERIFY.PHONE) {
      try {
        const credential = PhoneAuthProvider.credential(verificationId, otp);

        await signInWithCredential(auth, credential);

        onChangeCallback?.(true);

        toast.success('Xác thực thành công');

        navigate(AuthRouteKeys.NewPassword);
      } catch (error) {
        toast.error('Mã xác thực không hợp lệ. Vui lòng thử lại.');
      }
    } else if (methodVerify == TYPE_VERIFY.EMAIL) {
      try {
        var phoneNumber = getPhoneNumber('phoneNumber');
        const res = await mutateAsyncVerifyOtpByEmail({
          phoneNumber: formatPhoneNumber(defaultPhoneNo || phoneNumber.value),
          otp,
        });
        onChangeCallback?.(true);
        toast.success('Xác thực thành công');
        navigate(AuthRouteKeys.NewPasswordVerifByEmail, {
          state: { token: res.token },
        });
      } catch (error) {
        // toast.error("Mã xác thực không hợp lệ. Vui lòng thử lại.");
      }
    }
  };

  const {
    handleSubmit: onSubmitFN,
    getInputProps: getPhoneNumber,
    isSubmitting: isSubmitPN,
  } = useForm({
    structure: formSchemaPN(defaultPhoneNo),
    onSubmit: handleVerifyPhoneNumber,
  });

  const {
    handleSubmit: onSubmitOtp,
    getInputProps: getOtp,
    isSubmitting: isSubmitOTP,
  } = useForm({
    structure: formSchemaOTP,
    onSubmit: handleSubmitOTP,
  });

  return (
    <>
      <div className='login-form'>
        <div className='login-form__logo'>
          <img
            style={{ width: '288px' }}
            className='login-form__logo__img'
            src='/assets/images/logo-white.png'
            alt=''
          />
        </div>

        <form onSubmit={onSubmitFN}>
          <InputWraper
            label={
              defaultPhoneNo ? 'Xác nhận số điện thoại' : 'Nhập số điện thoại'
            }
            inputProps={getPhoneNumber('phoneNumber')}
            onChangeCallback={setPhoneNumber}
            renderInput={(props) => {
              return <InputText {...props} isReadOnly={!!defaultPhoneNo} />;
            }}
          />

          {!verificationId && (
            <div className='login-form__actions'>
              <Button
                label='Gửi mã xác thực'
                type='submit'
                isLoading={isSubmitPN || isFetchingCheckPhoneNoQuery}
              />
              <Button
                label='Đổi tài khoản'
                type='button'
                onClick={handelChangePhoneNumber}
                buttonType='primary-outline'
              />
            </div>
          )}
        </form>

        {verificationId && (
          <form onSubmit={onSubmitOtp}>
            <InputWraper
              label={'OTP'}
              inputProps={getOtp('otp')}
              component={InputPinCode}
            />
            {verificationId && (
              <div className='textCenter text-verify'>
                OTP đã được gửi tới{' '}
                {methodVerify == TYPE_VERIFY.EMAIL
                  ? ` Email: ${verificationId}`
                  : ` SĐT: ${getPhoneNumber('phoneNumber').value}`}
              </div>
            )}
            <div className='login-form__actions'>
              <Button
                label='Xác nhận'
                type='submit'
                buttonType='success'
                isLoading={isSubmitOTP}
              />

              <Button
                label={
                  countdown > 0 ? `Gửi lại mã sau ${countdown}s` : 'Gửi lại mã'
                }
                disabled={countdown > 0}
                onClick={handleResendOTP}
                type='button'
                buttonType='info-outline'
                isLoading={isSubmitPN}
              />
            </div>

            {!onlyAllowPhoneNo && (
              <div className='textCenter'>
                <Button
                  label={
                    'Xác thực OTP bằng ' +
                    (methodVerify == TYPE_VERIFY.EMAIL
                      ? 'Số điện thoại'
                      : 'Email')
                  }
                  type='button'
                  onClick={() => {
                    if (methodVerify == TYPE_VERIFY.EMAIL) {
                      setMethoVerify(TYPE_VERIFY.PHONE);
                    } else if (methodVerify == TYPE_VERIFY.PHONE) {
                      setMethoVerify(TYPE_VERIFY.EMAIL);
                    }
                    setChangeMethodVerify(true);
                  }}
                  buttonType='primary-outline'
                />
              </div>
            )}
            <div className='textCenter'>
              <Button
                label='Đổi tài khoản'
                type='button'
                onClick={handelChangePhoneNumber}
                buttonType='primary-outline'
              />
            </div>
          </form>
        )}
      </div>

      <div id={`recaptcha-container`} />
    </>
  );
};

export default GetOTP;
